"use client";

import { Fragment, useEffect, useState } from "react";
import {
  Box,
  CardMedia,
  Typography,
  Grid,
  Container,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fade,
  Zoom,
  Link,
} from "@mui/material";
import Images from "assets/images/Images";
import { useTranslation } from "react-i18next";
import { useLanguage } from "context/UseContext";
import AddressServices from "apis/address/AddressServices";
import { ErrorToaster } from "components/toaster/Toaster";
import Helmet from "react-helmet";
import MailIcon from "@mui/icons-material/Mail";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SecurityIcon from "@mui/icons-material/Security";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GetAppIcon from "@mui/icons-material/GetApp";
import { keyframes } from "@mui/system";
import Colors from "assets/style/Colors";

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

const subtleGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0,0,0,0.1); }
  50% { box-shadow: 0 0 30px rgba(0,0,0,0.15); }
`;

function ContactUs() {
  const { lang } = useLanguage();
  const { t } = useTranslation();
  const [about, setAbout] = useState();
  const [expandedFaq, setExpandedFaq] = useState(false);

  const faqs = [
    {
      question: t("faq1"),
      answer:
      t("faqans1"),
      icon: <VerifiedIcon />,
    },
    {
      question: t("faq2"),
      answer:
      t("faqans2"),
      icon: <GetAppIcon />,
    },
    {
      question: t("faq3"),
      answer:
      t("faqans3"),
      icon: <SecurityIcon />,
    },
    {
      question:t("faq4"),
      answer:       t("faqans4"),
      
      icon: <AccessTimeIcon />,
    },
  ];

  const getAbout = async () => {
    try {
      const { responseCode, data } = await AddressServices.getAbout();
      if (responseCode === 200) {
        setAbout(data.result);
      }
    } catch (error) {
      ErrorToaster(lang === "ar" ? error?.messageAr : error?.message);
    }
  };

  useEffect(() => {
    getAbout();
  }, []);

  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  return (
    <Fragment>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Get in Touch with Scriptio | Contact Us</title>
        <meta
          name="description"
          content="Contact Scriptio for telehealth support, business inquiries, and questions. Licensed UAE telehealth platform with 24/7 customer service."
        />
        <link rel="canonical" href="https://e-rx.info/contact-us" />
      </Helmet>

      <CardMedia
        image={Images.about}
        sx={{
          width: "100%",
          height: { xs: "200px", md: "400px" },
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          position: "relative",
          pt: { xs: 16, md: 0 },
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: "20%",
            left: "10%",
            width: "40px",
            height: "40px",
            background: "rgba(255, 255, 255, 0.08)",
            borderRadius: "50%",
            animation: `${float} 8s ease-in-out infinite`,
            backdropFilter: "blur(5px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "60%",
            right: "15%",
            width: "30px",
            height: "30px",
            background: "rgba(255, 255, 255, 0.06)",
            borderRadius: "20%",
            animation: `${float} 6s ease-in-out infinite 3s`,
            backdropFilter: "blur(5px)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            textAlign: "center",
            zIndex: 2,
            width: "100%",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: "white",
              fontSize: { xs: "2.5rem", md: "4rem" },
              fontWeight: 700,
              mb: 2,
              textShadow: "0 2px 15px rgba(0,0,0,0.3)",
            }}
          >
            {t("getIn")}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "rgba(255,255,255,0.95)",
              fontSize: { xs: "1.1rem", md: "1.5rem" },
              fontWeight: 400,
              textShadow: "0 1px 8px rgba(0,0,0,0.2)",
            }}
          >
            {t("getInPara")}

          </Typography>
        </Box>
      </CardMedia>

      <Box
        sx={{
          background: "linear-gradient(180deg, #fafbfc 0%, #ffffff 100%)",
          minHeight: "100vh",
          py: 8,
        }}
      >
        <Container maxWidth="xl">
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 6,
              borderRadius: 3,
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(69, 90, 120, 0.1)",
              textAlign: "center",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 3,
                color: Colors.tertiary,
                fontSize: { md: "46px", xs: "34px" },
              }}
            >
             {t("weValue")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.1rem",
                color: "#6b7280",
                maxWidth: "800px",
                mx: "auto",
                lineHeight: 1.7,
              }}
            >
            {t("weValuePara")}
            </Typography>
          </Paper>

          <Grid container spacing={6}>
            {/* Left Side - Contact Methods */}
            <Grid item xs={12} lg={8}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <Fade in timeout={1000}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      border: "1px solid rgba(69, 90, 120, 0.12)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 25px rgba(69, 90, 120, 0.15)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Box
                        sx={{
                          background: Colors.tertiary,
                          p: 2,
                          borderRadius: 2,
                          mr: 3,
                        }}
                      >
                        <SupportAgentIcon
                          sx={{ color: "white", fontSize: 28 }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="h5"
                          fontWeight="600"
                          sx={{
                            color: Colors.tertiary,
                            fontSize: { md: "36px", xs: "26px" },
                          }}
                        >
                          {t("customerSupport")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          General Inquiries & Assistance
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 3,
                            bgcolor: "rgba(69, 90, 120, 0.04)",
                            borderRadius: 2,
                            border: "1px solid rgba(69, 90, 120, 0.08)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              bgcolor: "rgba(69, 90, 120, 0.08)",
                              transform: "scale(1.01)",
                            },
                          }}
                        >
                          <MailIcon
                            sx={{ color: Colors.tertiary, mr: 2, fontSize: 24 }}
                          />
                          <Box>
                            <Typography variant="subtitle1" fontWeight="600">
                              {t("email")}
                            </Typography>
                            <Typography
                            component="a"
                              variant="body2"
                              sx={{
                                color: "#455a78",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                              href={`mailto:contact@e-rx.info`}
                            >
                              
                              contact@e-rx.info
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 3,
                            bgcolor: "rgba(69, 90, 120, 0.04)",
                            borderRadius: 2,
                            border: "1px solid rgba(69, 90, 120, 0.08)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              bgcolor: "rgba(69, 90, 120, 0.08)",
                              transform: "scale(1.01)",
                            },
                          }}
                        >
                          <PhoneIcon
                            sx={{ color: Colors.tertiary, mr: 2, fontSize: 24 }}
                          />
                          <Box>
                            <Typography variant="subtitle1" fontWeight="600">
                              {t("phone")}
                            </Typography>
                            <Link
  href="https://wa.me/971585634647"
  target="_blank"
  rel="noopener noreferrer"
  
  sx={{ color: "#6b7280", cursor: "pointer" ,textDecoration:"underline !important"  }}
>
  <Typography variant="body2" >
    +971 58 563 46 47
  </Typography>
</Link>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        bgcolor: "rgba(120, 144, 156, 0.08)",
                        borderRadius: 2,
                        border: "1px solid rgba(120, 144, 156, 0.15)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <AccessTimeIcon
                          sx={{ color: Colors.tertiary, mr: 1 }}
                        />
                        <Typography
                          variant="subtitle2"
                          fontWeight="600"
                          sx={{ color: Colors.tertiary }}
                        >
                          {t("operatingHours")}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "#6b7280", mt: 1 }}
                      >
                       {t("operatingHoursPara")}
                      </Typography>
                    </Box>
                  </Paper>
                </Fade>

                <Fade in timeout={1200}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      border: "1px solid rgba(156, 125, 102, 0.12)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 25px rgba(156, 125, 102, 0.15)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Box
                        sx={{
                          background: Colors.tertiary,
                          p: 2,
                          borderRadius: 2,
                          mr: 3,
                          animation: `${pulse} 3s ease-in-out infinite 1s`,
                        }}
                      >
                        <BusinessIcon sx={{ color: "white", fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="h5"
                          fontWeight="600"
                          sx={{
                            color: Colors.tertiary,
                            fontSize: { md: "36px", xs: "26px" },

                          }}
                        >
                          {t("businessInquiries")}
                        </Typography>
                       
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 3,
                        bgcolor: "rgba(156, 125, 102, 0.04)",
                        borderRadius: 2,
                        border: "1px solid rgba(156, 125, 102, 0.08)",
                      }}
                    >
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        <strong>{t("email")}</strong> :contact@e-rx.info
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      {t("businessInquiriesPara")}
                      </Typography>
                     
                    </Box>
                  </Paper>
                </Fade>

                <Fade in timeout={1400}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #f6f9f7 100%)",
                      border: "1px solid rgba(102, 126, 108, 0.12)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 25px rgba(102, 126, 108, 0.15)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Box
                        sx={{
                          background: Colors.tertiary,
                          p: 2,
                          borderRadius: 2,
                          mr: 3,
                          animation: `${pulse} 3s ease-in-out infinite 2s`,
                        }}
                      >
                        <GetAppIcon sx={{ color: "white", fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="h5"
                          fontWeight="600"
                          sx={{ color: Colors.tertiary,
                            fontSize: { md: "36px", xs: "26px" },


                           }}
                        >
                          {t("downloadApp")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {t("downloadAppPara")}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Box
                        component="a"
                        href="https://apps.apple.com/ae/app/scriptio-online-pharmacy/id1539857878"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "inline-block",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={Images.appStore}
                          alt="Download on App Store"
                          sx={{
                            height: "50px",
                            width: "auto",
                            borderRadius: 2,
                            boxShadow: "0 3px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                      </Box>
                      <Box
                        component="a"
                        href="https://play.google.com/store/apps/details?id=com.erxprescriptionuser&pli=1"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "inline-block",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={Images.googlePlay}
                          alt="Get it on Google Play"
                          sx={{
                            height: "50px",
                            width: "auto",
                            borderRadius: 2,
                            boxShadow: "0 3px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Fade>
              </Box>
            </Grid>

            {/* Right Side - Image and Important Notice */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ position: "sticky", top: 100 }}>
                <Zoom in timeout={1000}>
                  <Paper
                    elevation={4}
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      mb: 4,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.01)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Images.contactus}
                      alt="Professional customer support"
                      sx={{
                        width: "100%",
                        height: "300px",
                        objectFit: "cover",
                      }}
                    />
                  </Paper>
                </Zoom>

                <Fade in timeout={1200}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      background:
                        "linear-gradient(135deg, #fef9f5 0%, #fdf4ed 100%)",
                      border: "2px solid #d97706",
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      sx={{ color: "#c2410c", mb: 2 ,
                        fontSize:{md:"30px",xs:"24px"}

                      }}
                    >
                      ⚠️ {t("impNotice")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#9a3412" }}>
                    {t("businessInquiriesPara2")}

                    </Typography>
                  </Paper>
                </Fade>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 8 }}>
            <Typography
              variant="h3"
              sx={{
                textAlign: "center",
                fontWeight: 700,
                mb: 6,
                color: Colors.tertiary,
                fontSize:{md:"46px",xs:"34px"}

              }}
            >
              {t("frequentlyAsked")}
            </Typography>

            <Box sx={{ mx: "auto" }}>
              {faqs.map((faq, index) => (
                <Accordion
                  key={index}
                  expanded={expandedFaq === `panel${index}`}
                  onChange={handleFaqChange(`panel${index}`)}
                  sx={{
                    mb: 2,
                    borderRadius: "12px !important",
                    overflow: "hidden",
                    border: "1px solid rgba(69, 90, 120, 0.1)",
                    "&:before": { display: "none" },
                    boxShadow: "0 3px 15px rgba(0,0,0,0.06)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      background:
                        expandedFaq === `panel${index}`
                          ? "linear-gradient(135deg, #f1f5f9 0%, #f8fafc 100%)"
                          : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      minHeight: "70px",
                      "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          background: Colors.tertiary,
                          p: 1.5,
                          borderRadius: 2,
                          mr: 3,
                          color: "white",
                        }}
                      >
                        {faq.icon}
                      </Box>
                      <Typography variant="h6" fontWeight="600" sx={{
                                          fontSize:{md:"26px",xs:"20px"}

                      }}>
                        {faq.question}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      background:
                        "linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)",
                      p: 4,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#6b7280",
                        lineHeight: 1.7,
                        fontSize: "1.05rem",
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Fragment>
  );
}

export default ContactUs;
